import React from 'react';
import { cn } from '@/lib/utils';
import { Lock, TrendingUp, Star, Clock, TrendingDown, HelpCircle } from 'lucide-react';

export type MaturityType = 'hold' | 'drink-or-hold' | 'drink' | 'drink-soon' | 'past-peak' | 'unknown';

interface MaturityIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  maturity: MaturityType;
  disabled?: boolean;
}

export const MaturityIndicator = React.forwardRef<HTMLDivElement, MaturityIndicatorProps>(
  ({ maturity, disabled, className, ...props }, ref) => {
    const config: Record<MaturityType, { color: string; Icon: typeof Lock; label: string }> = {
      'hold': {
        color: 'var(--rc-maturity-hold)',
        Icon: Lock,
        label: 'Hold'
      },
      'drink-or-hold': {
        color: 'var(--rc-maturity-drink-or-hold)',
        Icon: TrendingUp,
        label: 'Drink or Hold'
      },
      'drink': {
        color: 'var(--rc-maturity-drink)',
        Icon: Star,
        label: 'Drink'
      },
      'drink-soon': {
        color: 'var(--rc-maturity-drink-soon)',
        Icon: Clock,
        label: 'Drink Soon'
      },
      'past-peak': {
        color: 'var(--rc-maturity-past-peak)',
        Icon: TrendingDown,
        label: 'Past Peak'
      },
      'unknown': {
        color: 'var(--rc-maturity-unknown)',
        Icon: HelpCircle,
        label: 'Unknown'
      }
    };

    const { color, Icon, label } = config[maturity];

    return (
      <div
        ref={ref}
        role="img"
        aria-label={`Maturity: ${label}`}
        className={cn(
          "inline-flex items-center gap-[var(--rc-space-sm)] transition-opacity duration-200 min-w-[24px] min-h-[24px]",
          disabled && "opacity-40",
          className
        )}
        {...props}
      >
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <Icon
          className="w-4 h-4 flex-shrink-0"
          style={{ color: color }}
          strokeWidth={1.5}
        />
      </div>
    );
  }
);

MaturityIndicator.displayName = 'MaturityIndicator';
